/**
 * TaskItem Component
 * 
 * Individual task display within a routine timeline.
 * Shows time range, task name, and edit button.
 */

import React from 'react';
import styles from './TaskItem.module.scss';

interface Task {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  duration?: number;
  icon?: string;
}

interface TaskItemProps {
  task: Task;
  onEdit?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  onEdit,
  isFirst = false,
  isLast = false,
}) => {
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} mins`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate duration from start and end time if not provided
  const calculateDuration = (): number => {
    if (task.duration !== undefined) return task.duration;
    const [startHours, startMins] = task.startTime.split(':').map(Number);
    const [endHours, endMins] = task.endTime.split(':').map(Number);
    return (endHours * 60 + endMins) - (startHours * 60 + startMins);
  };

  const duration = calculateDuration();

  return (
    <div 
      className={`${styles.taskItem} ${isFirst ? styles.first : ''} ${isLast ? styles.last : ''}`}
    >
      <div className={styles.timeRange}>
        <span className={styles.time}>
          {formatTime(task.startTime)} - {formatTime(task.endTime)}
        </span>
      </div>
      
      <div className={styles.content}>
        {task.icon && <span className={styles.icon}>{task.icon}</span>}
        <span className={styles.name}>{task.name}</span>
        <span className={styles.duration}>{formatDuration(duration)}</span>
      </div>
      
      <button
        className={styles.editButton}
        onClick={onEdit}
        aria-label={`Edit ${task.name}`}
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path 
            d="M11.5 2.5L13.5 4.5M2 14L2.5 11.5L11 3L13 5L4.5 13.5L2 14Z" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default TaskItem;
